<template>
  <div>
    <ErrorBoundary>
      <EventGiftcardBookingIcons
        :first-label="labels[0]"
        :second-label="labels[1]"
        :third-label="labels[2]"
      />
      <Button
        :variant="isPrimaryButton || enableEmbedded ? 'contained' : 'outlined'"
        class="mt-4 ease duration-300 w-full py-3"
        :style="style"
        :data-test-id="ctaTestId"
        @click="gift"
      >
        {{
          bookGiftcardText
            ? bookGiftcardText
            : $t('product.show.checkout.bookGiftcard')
        }}
      </Button>
    </ErrorBoundary>
  </div>
</template>

<script setup lang="ts">
import type {
  EventDate,
  EventDescription,
  Pricings,
} from '~/composables/types/event'
import { NUMBER_OF_EVENTS } from '~/composables/product/constants'
import type { CartItem } from '~/composables/types/cart'
import { useGiftcardAddToCart } from '~/composables/useGiftcardAddToCart'

const { addGiftcardToCart } = useGiftcardAddToCart()
const { t, locale } = useI18n()
const {
  enableEmbedded,
  enableSubdomain,
  bookGiftcardText,
  // sectionTitle,
  selectedTime,
  isPrimaryButton,
  textColor,
  ctaBackgroundColor,
  pricings,
  ctaTestId,
} = defineProps<{
  enableEmbedded?: boolean
  enableSubdomain?: boolean | null
  bookGiftcardText?: string
  // sectionTitle?: string
  selectedTime?: EventDate
  isPrimaryButton?: boolean
  textColor?: string
  ctaBackgroundColor?: string
  pricings: Pricings
  // Optional stable hook for the CTA button (e.g. A/B experiment targeting)
  ctaTestId?: string
}>()

const emit = defineEmits<{
  (e: 'add-item-to-cart', cartItem: CartItem): void
}>()

const eventData = inject<EventDescription>('eventData')!
const isSpecialGiftcardEvent = inject('isSpecialGiftcardEvent') as boolean

const style = computed(() => {
  return {
    ...(textColor && { color: `#${textColor}` }),
    ...(ctaBackgroundColor && {
      backgroundColor: `#${ctaBackgroundColor}`,
    }),
  }
})

const gift = async () => {
  const cartItem = await addGiftcardToCart({
    event: eventData,
    pricings,
    giftcardType: isSpecialGiftcardEvent ? 'SPECIAL' : 'EVENT',
    selectedTime,
    clearCartFirst: enableEmbedded,
  })

  if (cartItem) {
    emit('add-item-to-cart', cartItem)
  }
}

const eventCount = formattedNumberOfEvents(locale.value, NUMBER_OF_EVENTS)
const labels = computed(() => {
  if (enableEmbedded || enableSubdomain) {
    return [
      t('product.show.checkout.embeddedGiftcard.iconsLabels.first'),
      t('product.show.checkout.embeddedGiftcard.iconsLabels.second'),
      t('product.show.checkout.embeddedGiftcard.iconsLabels.third'),
    ]
  } else {
    return [
      t('product.show.checkout.giftcard.validity'),
      t('product.show.checkout.giftcard.forAnyKonfetti', { eventCount }),
      t('product.show.checkout.giftcard.giftExperiences'),
    ]
  }
})
</script>
